import React, { useState, useEffect } from 'react'
import EditableCell from '../../EditableCell'
import EditableFixedCell from '../../EditableFixedCell'
import CreatableSelectCell from './CreatableSelectCell'
import { eventBus, EVENTS } from '../../../../../utils/EventBus'
import { formatPopupDateTime } from '../../../arrivalInfo'

const isHalfWidthChar = (char) => /^[\u0020-\u007e\uff61-\uff9f]$/.test(char)
const isFullWidthChar = (char) => !isHalfWidthChar(char)

const isValidShippingMarkInput = (value) => {
  const chars = Array.from(String(value ?? '').trim())
  if (chars.length === 0) return true

  const hasHalfWidth = chars.some(isHalfWidthChar)
  const hasFullWidth = chars.some(isFullWidthChar)
  if (hasHalfWidth && hasFullWidth) return false

  if (hasFullWidth) return chars.length <= 4
  return chars.length <= 8
}

const isValidGradeOrSizeInput = (value) => {
  const chars = Array.from(String(value ?? '').trim())
  if (chars.length === 0) return true

  const hasHalfWidth = chars.some(isHalfWidthChar)
  const hasFullWidth = chars.some(isFullWidthChar)
  if (hasHalfWidth && hasFullWidth) return false

  if (hasFullWidth) return chars.length <= 3
  return chars.length <= 5
}

const normalizeIntegerInput = (value) => {
  if (value === undefined || value === null || value === '') return ''
  return String(Number.parseInt(String(value), 10) || 0)
}

const normalizeNumberInput = (value) => {
  if (value === undefined || value === null || value === '') return ''
  const strVal = String(value).trim()
  if (strVal === '') return ''
  const parsed = Number(strVal)
  return Number.isNaN(parsed) ? '' : String(parsed)
}

const normalizeGradeOrSizeInput = (value) => {
  const text = String(value ?? '').trim()
  if (/^\d{1,4}$/.test(text)) return `.${text}`
  return text
}

const getProductName = (item) =>
  [
    item.product_name,
    item.PRODUCT_NAME,
    item.productName,
    item.hinmeiName,
    item.hinmei_name
  ]
    .find((value) => String(value ?? '').trim())
    ?.toString()
    .trim() || ''

const getPriceDisplayValue = (item, field) => {
  const fixedField = `__fixed_${field}`
  if (Object.prototype.hasOwnProperty.call(item, fixedField)) {
    return item[field] ?? ''
  }
  return Number(item[field]) === 0 ? '' : item[field]
}

const ArrivalDateCell = ({ value, showTime = true }) => {
  const dateTime = formatPopupDateTime(value)
  if (dateTime === '-') return '-'

  return (
    <div className='flex flex-col items-center leading-tight'>
      <span>{dateTime.date}</span>
      {showTime && <span className='text-[8px]'>{dateTime.time}</span>}
    </div>
  )
}

const TableRow = ({
  item,
  rowIdx,
  isLast,
  customerColumns,
  selectedColumn,
  selectedColumnIds = [],
  selectedRowIndexes = [],
  handleRowSelection,
  distributionManager,
  getStickyLeft,
  buyerLeft,
  showTooltip,
  setShowTooltip,
  handleFixedCellChange,
  dropTargetId,
  isAtEnd,
  handleRowDragStart,
  handleRowDragEnd,
  handleRowDragOver,
  handleRowDrop,
  draggedRowKey,
  draggedRowIdx,
  dropTargetRowIdx,
  handleDrop,
  handleRowContextMenu,
  hiddenBefore = [],
  hiddenAfter = [],
  onUnhide,
  parentItem,
  isFirstInGroup = true,
  groupSize = 1,
  priceId,
  shippingMarkOptions = [],
  gradeOptions = [],
  sizeOptions = [],
  rowReorderDisabled = false,
  isReadOnly = false
}) => {
  const [, forceUpdate] = useState(0)

  useEffect(() => {
    const unsub1 = eventBus.on(EVENTS.DISTRIBUTION_CHANGED, () =>
      forceUpdate((v) => v + 1)
    )
    const unsub2 = eventBus.on(EVENTS.DISTRIBUTION_COMMITTED, () =>
      forceUpdate((v) => v + 1)
    )
    const unsub3 = eventBus.on(EVENTS.CELL_VALUE_CHANGED, (data) => {
      if (
        data.hinmeiCode === item.hinmeiCode &&
        String(data.niukabango) === String(item.niukabango)
      ) {
        forceUpdate((v) => v + 1)
      }
    })
    return () => {
      unsub1()
      unsub2()
      unsub3()
    }
  }, [item.hinmeiCode, item.niukabango])

  const remaining = distributionManager.calculateRemaining(item)
  const isNegative = remaining < 0

  const profit = distributionManager.calculateProfit(item)

  // Calculate 箱数 (box count) = 数量 / 入数
  const parseNumLocal = (val) => {
    const n = Number(
      String(val ?? '')
        .trim()
        .replace(/,/g, '')
    )
    return Number.isFinite(n) ? n : 0
  }
  const currentQuantityUnit = parseNumLocal(
    distributionManager.getFixedField(
      item.hinmeiCode,
      item.niukabango,
      'quantity_unit',
      item.quantity_unit
    )
  )
  const currentQuantityForBox = parseNumLocal(
    distributionManager.getFixedField(
      item.hinmeiCode,
      item.niukabango,
      'quantity',
      item.quantity ?? item.total_quantity ?? item.totalQuantity ?? 0
    )
  )
  const boxCount =
    currentQuantityUnit > 0
      ? Math.floor(currentQuantityForBox / currentQuantityUnit)
      : ''

  const isRowDragTarget = !rowReorderDisabled && dropTargetRowIdx === rowIdx
  const isLastRowTarget =
    !rowReorderDisabled && dropTargetRowIdx === rowIdx + 1 && draggedRowIdx !== null && isLast
  const isSelectedRow = selectedRowIndexes.includes(rowIdx)
  const productName = getProductName(item)

  const handleRowContextMenuEvent = (e, itemArg, menuType) => {
    if (isReadOnly) return
    e.preventDefault()
    e.stopPropagation()
    handleRowContextMenu(e, itemArg, rowIdx, priceId, menuType)
  }

  // Use the current row item for shared fields so split rows are independent.
  const sharedItem = item

  const getRowKey = (rowItem) => `${rowItem.hinmeiCode}::${rowItem.niukabango}`

  return (
    <tr
      key={`${item.hinmeiCode}-${item.niukabango}-${rowIdx}`}
      className={`${isRowDragTarget ? 'row-drag-over' : ''} ${isLastRowTarget ? 'row-drag-over-bottom' : ''} ${draggedRowKey && getRowKey(item) === draggedRowKey ? 'dragging-row' : ''} ${isSelectedRow ? 'selected-row' : ''}`}
      onClick={(e) => {
        // Only select row when user clicks the "No." column (first column).
        // Prevents selecting the whole row when clicking other cells.
        const clickedTh = e.target.closest('td, th')
        const clickedTd = clickedTh
        const isNoColumn = clickedTd?.classList?.contains('row-number')

        if (!isNoColumn) return
        handleRowSelection(rowIdx, item, e)
      }}
      onDragOver={isReadOnly || rowReorderDisabled ? undefined : (e) => handleRowDragOver(e, rowIdx)}
      onDrop={
        isReadOnly || rowReorderDisabled
          ? undefined
          : (e) => {
              // handleDrop handles column reorder — must be called before handleRowDrop
              // because handleRowDrop calls e.stopPropagation() which would block bubbling.
              // handleDrop has its own guard (draggedColumnId check) so it's safe to always call.
              if (handleDrop) handleDrop(e, null)
              handleRowDrop(e, rowIdx)
            }
      }
      data-row-idx={rowIdx}
      data-row-hinmei-code={item.hinmeiCode}
      data-row-niukabango={item.niukabango}
    >
      <td
        className='row-number sticky-col text-[10px] group'
        style={{ left: 0, zIndex: 2, cursor: 'pointer', position: 'relative' }}
        draggable={!isReadOnly && !rowReorderDisabled}
        onDragStart={
          isReadOnly || rowReorderDisabled ? undefined : (e) => handleRowDragStart(e, rowIdx)
        }
        onDragEnd={isReadOnly || rowReorderDisabled ? undefined : handleRowDragEnd}
        onContextMenuCapture={(e) =>
          handleRowContextMenuEvent(e, item, 'row_first_col')
        }
      >
        {hiddenBefore.length > 0 && (
          <div
            className='absolute top-0 left-0 right-0 h-2 flex items-center justify-center bg-blue-100 hover:bg-blue-300 transition-colors z-10'
            onClick={(e) => {
              e.stopPropagation()
              if (isReadOnly) return
              hiddenBefore.forEach((k) => onUnhide(k))
            }}
            title='隠れている行を表示'
          >
            <svg
              width='8'
              height='8'
              viewBox='0 0 24 24'
              fill='none'
              stroke='currentColor'
              strokeWidth='4'
              strokeLinecap='round'
              strokeLinejoin='round'
            >
              <path d='M7 11l5-5 5 5M7 18l5-5 5 5' />
            </svg>
          </div>
        )}

        {rowIdx + 1}

        {hiddenAfter.length > 0 && (
          <div
            className='absolute bottom-0 left-0 right-0 h-2 flex items-center justify-center bg-blue-100 hover:bg-blue-300 transition-colors z-10'
            onClick={(e) => {
              e.stopPropagation()
              if (isReadOnly) return
              hiddenAfter.forEach((k) => onUnhide(k))
            }}
            title='隠れている行を表示'
          >
            <svg
              width='8'
              height='8'
              viewBox='0 0 24 24'
              fill='none'
              stroke='currentColor'
              strokeWidth='4'
              strokeLinecap='round'
              strokeLinejoin='round'
            >
              <path d='M7 13l5 5 5-5M7 6l5 5 5-5' />
            </svg>
          </div>
        )}
      </td>

      {isFirstInGroup && (
        <td
          className='data-cell sticky-col text-[10px] px-0'
          style={{
            textAlign: 'center',
            left: `${getStickyLeft(1)}px`,
            zIndex: 2
          }}
          title={item.receipt_number || ''}
          rowSpan={groupSize}
        >
          {item.shipper_code || '-'}
        </td>
      )}

      <td
        className='data-cell sticky-col text-[10px] px-0'
        style={{
          textAlign: 'center',
          fontWeight: 'bold',
          position: 'relative',
          left: `${getStickyLeft(2)}px`,
          zIndex: 2
        }}
        title={productName || undefined}
      >
        {item.hinmeiCode || '-'}
        {/* {showTooltip === item.hinmeiCode && (
          <div className='rp-tooltip'>{item.hinmeiCode} - 仮荷割</div>
        )} */}
      </td>

      {/* 荷印 (shipping_mark) - editable creatable select */}
      {isFirstInGroup && (
        <EditableFixedCell
          item={item}
          field='shipping_mark'
          value={item.shipping_mark || ''}
          onChange={handleFixedCellChange}
          distributionManager={distributionManager}
          type='text'
          numericOnly={false}
          validateValue={isValidShippingMarkInput}
          inputStyle={{
            textAlign: 'center',
            position: 'relative',
            left: `${getStickyLeft(3)}px`,
            zIndex: 2
          }}
          inputClassName='sticky-col text-[10px] px-0'
          readOnly={isReadOnly}
        />
      )}

      {isFirstInGroup && (
        <td
          className='data-cell sticky-col text-[10px] px-0'
          style={{ textAlign: 'center', left: `${getStickyLeft(4)}px`, zIndex: 2 }}
          rowSpan={groupSize}
        >
          <ArrivalDateCell value={item.arrival_shipping_at} showTime={false} />
        </td>
      )}

      {isFirstInGroup && (
        <td
          className='data-cell sticky-col text-[10px] px-0'
          style={{ textAlign: 'center', left: `${getStickyLeft(5)}px`, zIndex: 2 }}
          rowSpan={groupSize}
        >
          <ArrivalDateCell value={item.arrival_received_at} />
        </td>
      )}

      {/* 等級 (grade) - editable creatable select */}
      {isFirstInGroup && (
        <CreatableSelectCell
          item={sharedItem}
          field='grade'
          value={sharedItem.grade || ''}
          onChange={handleFixedCellChange}
          options={gradeOptions}
          validateValue={isValidGradeOrSizeInput}
          normalizeOnCommit={normalizeGradeOrSizeInput}
          distributionManager={distributionManager}
          rowSpan={groupSize}
          cellStyle={{
            left: `${getStickyLeft(6)}px`,
            zIndex: 2
          }}
          readOnly={isReadOnly}
        />
      )}

      {/* 階級 (size) - editable creatable select */}
      {isFirstInGroup && (
        <CreatableSelectCell
          item={sharedItem}
          field='size'
          value={sharedItem.size || ''}
          onChange={handleFixedCellChange}
          options={sizeOptions}
          validateValue={isValidGradeOrSizeInput}
          normalizeOnCommit={normalizeGradeOrSizeInput}
          distributionManager={distributionManager}
          rowSpan={groupSize}
          cellStyle={{
            left: `${getStickyLeft(7)}px`,
            zIndex: 2
          }}
          readOnly={isReadOnly}
        />
      )}

      <td
        className='data-cell sticky-col text-[10px] px-0'
        style={{
          textAlign: 'center',
          left: `${getStickyLeft(8)}px`,
          zIndex: 2
        }}
      >
        {item.package || '-'}
      </td>

      <td
        className='data-cell sticky-col text-[10px] px-0'
        style={{
          textAlign: 'center',
          left: `${getStickyLeft(9)}px`,
          zIndex: 2
        }}
      >
        {item.weight || '-'}
      </td>

      <EditableFixedCell
        item={item}
        field='quantity_unit'
        value={item.quantity_unit || ''}
        onChange={handleFixedCellChange}
        distributionManager={distributionManager}
        type='text'
        inputStyle={{
          textAlign: 'center',
          left: `${getStickyLeft(10)}px`,
          zIndex: 2
        }}
        inputClassName='sticky-col text-[12px] px-0'
        maxLength={3}
        normalizeOnCommit={normalizeIntegerInput}
        readOnly={isReadOnly}
      />

      {/* 箱数 - 読み取り専用 (read-only, calculated: 数量 / 入数) */}
      <td
        className='data-cell sticky-col text-[12px] px-0'
        style={{
          textAlign: 'center',
          left: `${getStickyLeft(11)}px`,
          zIndex: 2
        }}
      >
        {boxCount !== '' ? boxCount : ''}
      </td>

      <EditableFixedCell
        item={item}
        field='quantity'
        value={item.quantity ?? ''}
        onChange={handleFixedCellChange}
        distributionManager={distributionManager}
        type='text'
        inputStyle={{
          textAlign: 'center',
          left: `${getStickyLeft(12)}px`,
          zIndex: 2
        }}
        inputClassName='sticky-col text-[12px] px-0'
        maxLength={5}
        normalizeOnCommit={normalizeIntegerInput}
        readOnly={isReadOnly}
      />

      <td
        className='data-cell sticky-col text-[12px] px-0 sticky-shadow'
        style={{
          textAlign: 'center',
          fontWeight: 'bold',
          backgroundColor: isNegative ? '#ffebee' : '#e8f5e8',
          color: isNegative ? '#c62828' : '#2e7d32',
          left: `${getStickyLeft(13)}px`,
          zIndex: 2
        }}
      >
        {remaining}
      </td>

      <EditableFixedCell
        item={item}
        field='listing_quantity'
        value={item.listing_quantity || ''}
        onChange={handleFixedCellChange}
        distributionManager={distributionManager}
        type='text'
        inputStyle={{
          textAlign: 'center',
          left: `${getStickyLeft(14)}px`,
          zIndex: 2
        }}
        inputClassName='sticky-col text-[12px] px-0'
        maxLength={5}
        normalizeOnCommit={normalizeIntegerInput}
        readOnly={isReadOnly}
      />

      <EditableFixedCell
        item={item}
        field='input_price'
        value={getPriceDisplayValue(item, 'input_price')}
        onChange={handleFixedCellChange}
        distributionManager={distributionManager}
        type='text'
        numericOnly={true}
        inputStyle={{
          textAlign: 'center',
          left: `${getStickyLeft(15)}px`,
          zIndex: 2
        }}
        inputClassName='sticky-col text-[12px] px-0'
        normalizeOnCommit={normalizeNumberInput}
        readOnly={isReadOnly}
      />

      <EditableFixedCell
        item={item}
        field='output_price'
        value={getPriceDisplayValue(item, 'output_price')}
        onChange={handleFixedCellChange}
        distributionManager={distributionManager}
        type='text'
        numericOnly={true}
        inputStyle={{
          textAlign: 'center',
          left: `${getStickyLeft(16)}px`,
          zIndex: 2
        }}
        inputClassName='sticky-col text-[12px] px-0'
        normalizeOnCommit={normalizeNumberInput}
        readOnly={isReadOnly}
      />

      <td
        className='data-cell sticky-col text-[14px]'
        style={{
          textAlign: 'center',
          fontWeight: 'bold',
          color: profit >= 0 ? '#2e7d32' : '#c62828',
          left: `${getStickyLeft(17)}px`,
          zIndex: 2
        }}
      >
        {profit !== null ? profit.toLocaleString() : ''}
      </td>

      <td
        className='data-cell sticky-col sticky-shadow text-[14px]'
        style={{
          textAlign: 'center',
          left: `${buyerLeft}px`,
          zIndex: 2
        }}
      >
        {item.branch || '-'}
      </td>

      {customerColumns.map((col) => {
        const isSelectedCol = selectedColumnIds.includes(col.id)
        const isDropTarget = dropTargetId === col.id

        const originalValue = distributionManager.getOriginalValue(
          item,
          col.customerCode
        )

        return (
          <td
            key={col.id}
            className={`data-cell editable-cell ${isSelectedCol ? 'selected-column' : ''} ${
              isDropTarget ? (isAtEnd ? 'drag-over-right' : 'drag-over') : ''
            }`}
          >
            <EditableCell
              key={col.id}
              item={item}
              customerCode={col.customerCode}
              originalValue={originalValue}
              distributionManager={distributionManager}
              onFocus={() => setShowTooltip(null)}
              readOnly={isReadOnly}
            />
          </td>
        )
      })}
      <td className='data-cell'></td>
    </tr>
  )
}

export default TableRow
